import { formatMoney as formatMoneyHelper } from "@/scripts/global/utils/helpers";

const starterKitSubscriptionWidget = function (config) {
    return {
        // ── Config (immutable, set from Liquid) ──────────────────────────
        subscribeProductId: config.subscribeProductId,
        subscribeProductHandle: config.subscribeProductHandle || "",
        subscribeVariants: Array.isArray(config.subscribeVariants) ? config.subscribeVariants : [],
        onetimeProductId: config.onetimeProductId,
        onetimeProductHandle: config.onetimeProductHandle || "",
        onetimeVariantId: config.onetimeVariantId,
        onetimeAvailable: !!config.onetimeAvailable,
        sellingPlanId: config.sellingPlanId,
        hasSellingPlan: !!config.hasSellingPlan,
        onetimePrice: Number(config.onetimePrice) || 0,
        moneyFormat: config.moneyFormat || "${{amount}}",

        // ── Reactive state ───────────────────────────────────────────────
        selectedPlan: "subscribe",
        subscribeVariantId: config.subscribeVariantId,

        init() {
            if (!this.hasSellingPlan) {
                this.selectedPlan = "onetime";
            }

            this.syncBodyState();

            // React to plan toggle: trigger gallery swap + external price sync
            this.$watch("selectedPlan", (newPlan, oldPlan) => {
                if (newPlan === oldPlan) return;
                this.syncBodyState();
                this.swapGalleryForPlan(newPlan);
                this.syncExternalPriceBlock();
            });

            // React to variant change: external price sync
            this.$watch("subscribeVariantId", () => this.syncExternalPriceBlock());

            // Initial sync after Alpine settles
            this.$nextTick(() => this.syncExternalPriceBlock());
        },

        // Toggle a body class so the shared psn-product-price snippet (and any
        // other site-wide block scoped to kit PDPs via .js-starter-kit-* hook
        // classes) can opt out of the bundle compare-at strike when the
        // customer flips to one-time. Confined to kit PDPs because the widget
        // only mounts there; non-kit pages never see this class.
        syncBodyState() {
            document.body.classList.toggle(
                "starter-kit-otp-selected",
                this.selectedPlan === "onetime"
            );
        },

        // ── Computed ─────────────────────────────────────────────────────
        get currentSubscribeVariant() {
            return (
                this.subscribeVariants.find(v => v.id === this.subscribeVariantId) ||
                this.subscribeVariants[0] ||
                { id: 0, price: 0, comparePrice: 0, available: false }
            );
        },

        get subscribePrice() {
            return Number(this.currentSubscribeVariant.price) || 0;
        },

        get subscribeComparePrice() {
            return Number(this.currentSubscribeVariant.comparePrice) || 0;
        },

        get isAvailable() {
            return this.selectedPlan === "subscribe"
                ? !!this.currentSubscribeVariant.available
                : !!this.onetimeAvailable && !!this.onetimeVariantId;
        },

        get activeVariantId() {
            return this.selectedPlan === "subscribe"
                ? this.subscribeVariantId
                : this.onetimeVariantId;
        },

        get activePrice() {
            return this.selectedPlan === "subscribe"
                ? this.subscribePrice
                : this.onetimePrice;
        },

        // ── Actions ──────────────────────────────────────────────────────
        selectSubscribeVariant(variantId) {
            this.subscribeVariantId = Number(variantId);
            this.updateURLForVariant(this.subscribeVariantId);
            // Auto-switch radio to subscribe when user picks a size
            if (this.selectedPlan !== "subscribe") {
                this.selectedPlan = "subscribe";
            }
        },

        updateURLForVariant(variantId) {
            if (!variantId) return;
            try {
                const url = new URL(window.location);
                url.searchParams.set("variant", variantId);
                history.replaceState({}, "", url);
            } catch (err) {
                console.warn("[starter-kit-widget] URL update failed", err);
            }
        },

        formatMoney(cents) {
            const fn = window.formatMoney || formatMoneyHelper;
            return fn(cents, this.moneyFormat);
        },

        // Sync the standalone product_price block (rendered by psn-product-price.liquid)
        // with our active price. Bound to parent Alpine in Liquid; we override textContent
        // because parent Alpine's selectedVariant/selectedPlan don't change as a side-effect
        // of our widget interactions, so x-text won't re-evaluate and overwrite us.
        syncExternalPriceBlock() {
            const el = document.querySelector(".js-product-price-final");
            if (!el) return;
            el.textContent = this.formatMoney(this.activePrice);
        },

        // ── Gallery swap ─────────────────────────────────────────────────
        // Delegates to the parent product Alpine instance's exposed helper.
        async swapGalleryForPlan(plan) {
            const handle =
                plan === "onetime"
                    ? this.onetimeProductHandle
                    : this.subscribeProductHandle;

            if (!handle) return;

            const productSection = document.getElementById("product-main");
            if (!productSection) return;

            const productAlpine =
                window.Alpine && productSection._x_dataStack
                    ? window.Alpine.$data(productSection)
                    : null;

            if (productAlpine && typeof productAlpine.swapGalleryToHandle === "function") {
                await productAlpine.swapGalleryToHandle(handle);
            }
        }
    };
};

export default starterKitSubscriptionWidget;
